import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { Billing, PaymentMethod } from "@/api.types";
import axios from "axios";
import { useSession } from "@/services/auth0/session";

export const usePaymentMethods = () => {
  const { customer, token } = useSession();

  return useQuery<Billing, Error>({
    queryKey: ["paymentMethods", customer?.id] as const,
    queryFn: async () => {
      if (!customer?.id || !token) throw new Error("Not authenticated");
      const response = await axios.get<Billing>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer.id}/payment-methods`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    enabled: !!customer?.id && !!token,
    retry: false,
  });
};

export const useMakePaymentMethodDefault = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: () => void;
}) => {
  const { customer, token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (methodId: string) => {
      const response = await axios.patch<PaymentMethod>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer?.id}/payment-methods/${methodId}/default`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["paymentMethods", customer?.id],
      });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useDeletePaymentMethod = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: () => void;
}) => {
  const { customer, token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (methodId: string) => {
      const response = await axios.delete<PaymentMethod>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer?.id}/payment-methods/${methodId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["paymentMethods", customer?.id],
      });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};
